import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { createEvent } from "@/lib/events";
import { fmtError } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import CoverImagePicker from "@/components/CoverImagePicker";
import LocationPicker from "@/components/LocationPicker";
import { MailCheck, MailWarning, RefreshCw } from "lucide-react";
import { CATEGORY_KEYS } from "@/lib/categories";
import SharePromptModal from "@/components/SharePromptModal";

// "min" attribute uses datetime-local format (no Z).
function nowLocalISO() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const CATEGORIES = CATEGORY_KEYS;

export default function CreateEventPage() {
  const navigate = useNavigate();
  const { user, emailVerified, resendConfirmation } = useAuth();
  const [form, setForm] = useState({
    title: "",
    category: "Outdoors",
    date_time: "",
    city: "",
    description: "",
    capacity: "",
    is_private: false,
    requires_approval: false,
    additional_notes: "",
    cover_url: null,
    latitude: null,
    longitude: null,
    exact_address: "",
    rough_area: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [shareModal, setShareModal] = useState(null); // populated with created event after save

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLocation = (loc) => setForm((f) => {
    const next = { ...f, ...loc };
    // Auto-fill the city when LocationPicker resolves one, but only when the
    // user hasn't typed something there themselves.
    if (loc && loc.city && (!f.city || f.city.trim() === "")) {
      next.city = loc.city;
    }
    return next;
  });

  const onResend = async () => {
    setResending(true);
    const res = await resendConfirmation();
    setResending(false);
    if (res.ok) toast.success("Confirmation email sent. Check your inbox.");
    else toast.error(res.error || "Could not resend.");
  };

  // Block the whole create UI behind email verification.
  if (user && !emailVerified) {
    return (
      <div className="bg-paper min-h-[calc(100vh-4rem)]">
        <div className="max-w-xl mx-auto px-6 md:px-10 pt-12 md:pt-16 pb-20">
          <div
            data-testid="verify-required"
            className="border border-neon/60 bg-neon/5 p-8 sm:p-10 flex flex-col gap-5"
          >
            <div className="w-14 h-14 border border-neon flex items-center justify-center">
              <MailWarning size={26} strokeWidth={1.6} className="text-ink" />
            </div>
            <span className="text-xs uppercase tracking-[0.3em] text-ink">Verify your email</span>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-none">
              Confirm to host.
            </h1>
            <p className="text-ink/75 leading-relaxed">
              Click the link we sent to <span className="text-ink">{user.email}</span> to
              verify your account. Hosting is unlocked the moment you confirm.
            </p>
            <p className="text-ink/55 text-sm">
              You can still browse the feed and RSVP without verifying.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <button
                type="button"
                onClick={onResend}
                disabled={resending}
                data-testid="resend-verify-email"
                className="inline-flex items-center gap-2 bg-neon text-ink px-4 py-3 text-xs font-bold uppercase tracking-widest hover:bg-paper transition-colors disabled:opacity-60"
              >
                <RefreshCw size={14} strokeWidth={2.2} />
                {resending ? "Sending…" : "Resend email"}
              </button>
              <Link
                to="/feed"
                data-testid="back-to-feed"
                className="inline-flex items-center gap-2 border border-ink text-ink hover:bg-paper hover:text-ink px-4 py-3 text-xs font-bold uppercase tracking-widest transition-colors"
              >
                Back to feed
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.date_time || !form.city || !form.description) {
      toast.error("Fill in the required fields.");
      return;
    }
    if (!user) { navigate("/login?next=/create"); return; }
    if (!emailVerified) {
      toast.error("Verify your email to host.");
      return;
    }
    // Belt-and-braces past-date guard: HTML `min` blocks most browsers, but
    // older Safari / programmatic edits can sneak through. Stop at JS level.
    if (form.date_time) {
      const dt = new Date(form.date_time);
      if (Number.isFinite(dt.getTime()) && dt.getTime() < Date.now()) {
        toast.error("Event date/time can't be in the past.");
        return;
      }
    }
    setSubmitting(true);
    try {
      const dtIso = new Date(form.date_time).toISOString();
      const id = await createEvent(
        {
          ...form,
          date_time: dtIso,
          capacity: form.capacity === "" ? null : Number(form.capacity),
        },
        user.id
      );
      toast.success("Event posted.");
      // Show the premium share prompt; user can dismiss to land on the event.
      setShareModal({
        id,
        title: form.title,
        category: form.category,
        city: form.city,
        cover_url: form.cover_url,
      });
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-paper min-h-[calc(100vh-4rem)]">
      <div className="max-w-2xl mx-auto px-6 md:px-10 pt-12 md:pt-16 pb-20">
        <span className="text-xs uppercase tracking-[0.3em] text-ink">Post an event</span>
        <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight mt-3 mb-8 leading-none">
          What are you<br /><span className="text-ink">putting on?</span>
        </h1>

        <div
          data-testid="email-verified-banner"
          className="mb-10 border border-ink/15 p-4 flex items-center gap-3 text-ink/70"
        >
          <MailCheck size={16} className="text-ink shrink-0" />
          <div className="text-sm">
            <p className="uppercase tracking-widest text-xs text-ink/55">Email verified</p>
            <p className="text-ink/75 mt-0.5">{user?.email}</p>
          </div>
        </div>

        <form onSubmit={onSubmit} data-testid="create-event-form" className="flex flex-col gap-8">
          <Field label="Title">
            <input type="text" required maxLength={120} value={form.title}
              onChange={(e) => set("title", e.target.value)}
              data-testid="event-title-input" placeholder="Sunday vinyl listening" className="chunky-input" />
          </Field>

          <Field label="Category">
            <select value={form.category} onChange={(e) => set("category", e.target.value)}
              data-testid="event-category-select" className="chunky-input bg-paper">
              {CATEGORIES.map((c) => <option key={c} value={c} className="bg-paper text-ink">{c}</option>)}
            </select>
          </Field>

          <Field label="Date & time">
            <input type="datetime-local" required min={nowLocalISO()} value={form.date_time}
              onChange={(e) => set("date_time", e.target.value)}
              data-testid="event-datetime-input" className="chunky-input" />
          </Field>

          <Field label="City / location">
            <input type="text" required maxLength={80} value={form.city}
              onChange={(e) => set("city", e.target.value)}
              data-testid="event-city-input" placeholder="Lisbon" className="chunky-input" />
          </Field>

          <Field label="Pin the spot on the map">
            <LocationPicker
              value={{
                latitude: form.latitude,
                longitude: form.longitude,
                exact_address: form.exact_address,
                rough_area: form.rough_area,
              }}
              onChange={setLocation}
              testidPrefix="loc"
            />
          </Field>

          <Field label="Description">
            <textarea required rows={5} maxLength={2000} value={form.description}
              onChange={(e) => set("description", e.target.value)}
              data-testid="event-description-input"
              placeholder="What's the vibe? What should people bring? When does it end?"
              className="chunky-input resize-y" />
          </Field>

          <Field label="Cover image (optional)">
            <CoverImagePicker
              value={form.cover_url}
              onChange={(url) => set("cover_url", url)}
              testidPrefix="cover"
            />
          </Field>

          <Field label="Capacity (optional)">
            <input type="number" min="1" max="9999" value={form.capacity}
              onChange={(e) => set("capacity", e.target.value)}
              placeholder="Leave empty for unlimited"
              data-testid="event-capacity-input" className="chunky-input" />
          </Field>

          <Toggle
            label="Private event"
            sublabel="Hidden from the public feed (you share the link)."
            testid="event-private-toggle"
            checked={form.is_private}
            onChange={(v) => set("is_private", v)}
          />

          <Toggle
            label="Approval required"
            sublabel="People request to join — you decide who's in."
            testid="event-approval-toggle"
            checked={form.requires_approval}
            onChange={(v) => set("requires_approval", v)}
          />

          <Field label="Additional notes (optional, visible to attendees)">
            <textarea rows={4} maxLength={2000} value={form.additional_notes}
              onChange={(e) => set("additional_notes", e.target.value)}
              placeholder="Meeting point, what to bring, parking, etc."
              data-testid="event-notes-input" className="chunky-input resize-y" />
          </Field>

          <button type="submit" disabled={submitting} data-testid="post-event-submit"
            className="bg-neon text-ink py-5 font-bold uppercase tracking-widest hover:bg-paper transition-colors disabled:opacity-60 mt-4">
            {submitting ? "Posting…" : "Post event"}
          </button>
        </form>
      </div>

      <style>{`
        .bare-input {
          background: transparent; border: none;
          border-bottom: 1px solid hsl(var(--border));
          color: #f4f4f0; padding: 12px 0; width: 100%;
          font-family: inherit; font-size: 1rem; outline: none;
          transition: border-color 200ms;
        }
        .bare-input:focus { border-bottom-color: #d6ff00; border-bottom-width: 2px; }
        .bare-input::placeholder { color: rgba(244,244,240,0.35); }
        select.bare-input { appearance: none; cursor: pointer; padding-right: 2rem;
          background-image: linear-gradient(45deg, transparent 50%, #d6ff00 50%), linear-gradient(-45deg, transparent 50%, #d6ff00 50%);
          background-position: calc(100% - 14px) 18px, calc(100% - 9px) 18px;
          background-size: 5px 5px, 5px 5px; background-repeat: no-repeat;
        }
        input[type="datetime-local"].bare-input { color-scheme: dark; }
      `}</style>

      {shareModal && (
        <SharePromptModal
          event={shareModal}
          onClose={() => {
            const id = shareModal.id;
            setShareModal(null);
            navigate(`/events/${id}`);
          }}
        />
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-xs uppercase tracking-widest text-ink/60">{label}</span>
      {children}
    </label>
  );
}

function Toggle({ label, sublabel, checked, onChange, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => onChange(!checked)}
      className="flex items-center justify-between gap-4 border border-ink/15 p-4 hover:border-ink transition-colors text-left"
    >
      <div className="flex flex-col gap-1">
        <span className="text-xs uppercase tracking-widest text-ink">{label}</span>
        <span className="text-xs text-ink/55">{sublabel}</span>
      </div>
      <span
        className={`w-11 h-6 shrink-0 relative transition-colors ${checked ? "bg-neon" : "bg-border"}`}
        aria-hidden
      >
        <span className={`absolute top-0.5 ${checked ? "left-[22px]" : "left-0.5"} w-5 h-5 bg-paper transition-all`} />
      </span>
    </button>
  );
}
